import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GET_PROJECT_SOV_TIME_SERIES } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { ProjectSovPoint } from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { getProjectSovTimeSeries } from '../../../services/project/stats/ProjectDailyStatsService';

/**
 * Time-series of Share of Voice % per brand keyword, bucketed by the active period.
 * Date keys align with the visibility time-series so both can be plotted on the same
 * X axis. Used by the V2 preview page to render the "Share of Voice over time" section.
 */
export const useGetProjectSovTimeSeries = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  period: MonitorPeriod | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && !!period && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const {
    data: sovTimeSeries,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ProjectSovPoint[]>({
    queryKey: [GET_PROJECT_SOV_TIME_SERIES, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
    queryFn: () => {
      return getProjectSovTimeSeries(projectId!, chatType!, period!, from, to, filterChatTypes, monitorPromptIds);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    sovTimeSeries: queryEnabled ? sovTimeSeries ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => {
      return queryClient.invalidateQueries({
        queryKey: [GET_PROJECT_SOV_TIME_SERIES, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
        refetchType: 'active',
      });
    },
  };
};
